/**
 * Computes the `date=` query param for caregiver calendar URLs (GMA-1233 +
 * default-month plan).
 *
 * - **Primary:** `Calendar.getOption("date")` is the library’s anchor date
 *   (not the first visible cell — `info.start` can be March 29 on an April month
 *   grid when weeks start Sunday).
 * - **Fallback:** `view.currentStart` vs `info.start` via `calendarPeriodAnchorYmd`
 *   when `getOption` is unavailable (tests, edge cases).
 *
 * Activity fetches should keep using `info.start` / `info.end` (visible range).
 */
import moment from "moment-timezone";

/**
 * @param {{ start?: Date | string | null; view?: { currentStart?: Date | string | null | undefined } } | null | undefined} info - `datesSet` payload from `@event-calendar/core`.
 * @returns {string} `YYYY-MM-DD`, or `""` when no anchor source exists (callers must not treat `moment(undefined)` as intentional "today").
 */
export function calendarPeriodAnchorYmd(info) {
  const anchorSource = info?.view?.currentStart ?? info?.start;
  if (anchorSource === null || anchorSource === undefined || anchorSource === "") {
    return "";
  }
  const m = moment(anchorSource);
  if (!m.isValid()) {
    return "";
  }
  return m.format("YYYY-MM-DD");
}

/**
 * Prefer the live @event-calendar instance’s `date` option; fall back to period math.
 *
 * @param {{ getOption?: (key: string) => unknown } | null | undefined} ec - Calendar API ref
 * @param {Parameters<typeof calendarPeriodAnchorYmd>[0]} datesSetInfo - `datesSet` callback arg
 * @returns {string} `YYYY-MM-DD` (never empty: if the payload has no anchor, uses caregiver-local today as last resort)
 */
export function calendarUrlAnchorYmd(ec, datesSetInfo) {
  let raw;
  try {
    raw = typeof ec?.getOption === "function" ? ec.getOption("date") : null;
  } catch {
    raw = null;
  }
  if (raw !== null && raw !== undefined) {
    const m = moment(raw);
    if (m.isValid()) return m.format("YYYY-MM-DD");
  }
  const period = calendarPeriodAnchorYmd(datesSetInfo);
  if (period) return period;
  return moment().format("YYYY-MM-DD");
}
